import {
  Clock,
  FileText,
  Folder,
  Plus,
  Share2,
  Sparkles,
  Star,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { Button, Menu, Progress, ScrollArea } from "@/components/ui";
import {
  VAULT_NAV_ITEMS,
  VAULT_SMART_FOLDERS,
  VAULT_SOURCES,
  VAULT_TOTAL_STORAGE,
} from "@/mockData/vaultNav";
import type { VaultNavId, VaultSourceId } from "@/types/vaultNav.types";
import { cn } from "@/lib/utils";

const NAV_ICONS: Record<VaultNavId, typeof Folder> = {
  all: Folder,
  recent: Clock,
  starred: Star,
  shared: Share2,
  trash: Trash2,
};

interface VaultSidebarProps {
  fileCount: number;
  activeNav: VaultNavId;
  activeSource: VaultSourceId;
  activeSmartFolder: string | null;
  onNavChange: (nav: VaultNavId) => void;
  onSourceChange: (source: VaultSourceId) => void;
  onSmartFolderChange: (id: string | null) => void;
  onNewFolder: () => void;
  onUpload: () => void;
  isDemo: boolean;
}

function NavRow({
  active,
  onClick,
  icon: Icon,
  label,
  count,
  accent,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Folder;
  label: string;
  count?: number;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "mb-0.5 flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
        active
          ? "bg-muted font-medium text-foreground"
          : " text-muted-foreground hover:bg-muted/60 hover:text-foreground",
        accent && active && "text-foreground",
      )}
    >
      <Icon className={cn("size-3.5 shrink-0", active && accent && "text-foreground")} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground tabular-nums">{count}</span>
      )}
    </button>
  );
}

export function VaultSidebar({
  fileCount,
  activeNav,
  activeSource,
  activeSmartFolder,
  onNavChange,
  onSourceChange,
  onSmartFolderChange,
  onNewFolder,
  onUpload,
  isDemo,
}: VaultSidebarProps) {
  const storagePct = (VAULT_TOTAL_STORAGE.usedGb / VAULT_TOTAL_STORAGE.totalGb) * 100;
  const activeSourceConfig =
    activeSource !== "all" ? VAULT_SOURCES.find((s) => s.id === activeSource && s.connected) : null;

  return (
    <aside className="flex w-60 shrink-0 flex-col overflow-hidden border-r border-border">
      <div className="shrink-0 border-b border-border px-4 py-3.5">
        <div className="mb-2.5 flex items-center gap-2">
          <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-[11px] font-semibold tracking-wide text-foreground">Vault</span>
          <span className="ml-auto font-mono text-[10px] text-muted-foreground tabular-nums">
            {fileCount.toLocaleString()} files
          </span>
        </div>
        <Menu>
          <Menu.Trigger className="w-full">
            <Button className="w-full gap-1.5" size="sm">
              <Plus className="size-3.5" aria-hidden="true" />
              New
            </Button>
          </Menu.Trigger>
          <Menu.Popup align="start" className="w-52">
            <Menu.Item onClick={onNewFolder}>
              <Folder className="size-4" aria-hidden="true" />
              New folder
            </Menu.Item>
            <Menu.Item onClick={onUpload}>
              <Upload className="size-4" aria-hidden="true" />
              Upload files
            </Menu.Item>
            <Menu.Separator />
            <Menu.Item onClick={() => toast.info("Ari organize is coming soon")}>
              <Sparkles className="size-4 text-primary" aria-hidden="true" />
              Ari: organize files
            </Menu.Item>
          </Menu.Popup>
        </Menu>
      </div>

      <ScrollArea className="min-h-0 flex-1 px-2 py-2.5">
        <div className="mb-3.5">
          {VAULT_NAV_ITEMS.map((item) => (
            <NavRow
              key={item.id}
              active={activeNav === item.id && !activeSmartFolder}
              onClick={() => {
                onNavChange(item.id);
                onSmartFolderChange(null);
              }}
              icon={NAV_ICONS[item.id]}
              label={item.label}
              count={isDemo ? item.count : undefined}
            />
          ))}
        </div>

        <div className="mb-3.5">
          <div className="flex items-center px-2.5 py-1.5">
            <span className="flex-1 text-xs font-semibold text-muted-foreground">
              Connected sources
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              className="size-5 text-muted-foreground"
              aria-label="Connect new source"
              onClick={() => toast.info("Connect integrations from Settings")}
            >
              <Plus className="size-2.5" aria-hidden="true" />
            </Button>
          </div>
          {VAULT_SOURCES.map((source) => {
            const isConnected = source.connected;
            const isActive = activeSource === source.id;
            return (
              <button
                key={source.id}
                type="button"
                disabled={!isConnected}
                onClick={() => {
                  if (!isConnected) return;
                  if (source.id === "eq" || source.id === "gdrive" || source.id === "dropbox") {
                    onSourceChange(source.id);
                    onSmartFolderChange(null);
                  }
                }}
                className={cn(
                  "mb-0.5 flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                  isActive
                    ? "bg-muted font-medium text-foreground"
                    : " text-muted-foreground hover:bg-muted/60",
                  !isConnected && "cursor-default opacity-60",
                )}
              >
                <span
                  className="size-2 shrink-0 rounded-sm"
                  style={{ backgroundColor: source.tone }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate">{source.name}</span>
                {isConnected ? (
                  <span className="size-1.5 shrink-0 rounded-full bg-success" title="Synced" />
                ) : (
                  <span className="text-[10px] text-muted-foreground">+</span>
                )}
              </button>
            );
          })}
          {activeSourceConfig?.usedGb !== undefined && activeSourceConfig.totalGb !== undefined && (
            <div className="px-2.5 pt-2">
              <div className="mb-1 flex justify-between text-[10px] text-muted-foreground">
                <span>{activeSourceConfig.usedGb} GB used</span>
                <span>of {activeSourceConfig.totalGb} GB</span>
              </div>
              <Progress
                value={(activeSourceConfig.usedGb / activeSourceConfig.totalGb) * 100}
                className="h-1"
              />
            </div>
          )}
        </div>

        {isDemo && (
          <div>
            <div className="px-2.5 py-1.5 text-xs font-semibold text-muted-foreground">
              Smart folders
            </div>
            {VAULT_SMART_FOLDERS.map((sf) => (
              <NavRow
                key={sf.id}
                active={activeSmartFolder === sf.id}
                onClick={() => {
                  onSmartFolderChange(sf.id);
                  onNavChange("all");
                }}
                icon={FileText}
                label={sf.label}
                count={sf.count}
                accent
              />
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="shrink-0 border-t border-border px-3.5 py-3">
        <p className="mb-1.5 text-[10px] text-muted-foreground">
          Total storage · {VAULT_TOTAL_STORAGE.usedGb} GB / {VAULT_TOTAL_STORAGE.totalGb} GB
        </p>
        <Progress value={storagePct} className="h-1.5" />
      </div>
    </aside>
  );
}
